import React, { useContext, useEffect, useState } from 'react'
import Recaptcha from 'react-recaptcha'
import { ThreeDots } from 'react-loader-spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-light-svg-icons'

import { onFormSubmitValidateRecaptcha } from '../form-utils'
import ModalContext from './modal-context'
import { FormRow, InputType } from './form-row'
import { useLoadRecaptcha } from '../util/hooks/useLoadRecaptcha'

const defaultFormData = {
  first_name: '',
  last_name: '',
  email: '',
  company_name: '',
  message: '',
  newsletter_signup: false,
  consent: false,
}

const isServer = typeof window === 'undefined'

interface IContactUsFormProps {
  isOpen: boolean
}

const ContactUsForm: React.FunctionComponent<IContactUsFormProps> = ({
  isOpen,
}) => {
  const { loadRecaptcha, recaptchaLoaded } = useLoadRecaptcha()

  const [
    {
      first_name,
      last_name,
      email,
      company_name,
      message,
      newsletter_signup,
      consent,
    },
    setFormData,
  ] = useState(defaultFormData)

  const [submitting, setSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState('')
  const [newsletterSubmitResult, setNewsletterSubmitResult] = useState('')
  const [success, setSuccess] = useState(false)

  const {
    'contact-us': { toggleIsOpen },
  } = useContext(ModalContext)

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  > = (event) => {
    submitResult && setSubmitResult('')
    newsletterSubmitResult && setNewsletterSubmitResult('')
    success && setSuccess(false)
    const { target } = event
    const value =
      target.type === 'checkbox'
        ? (target as HTMLInputElement).checked
        : target.value
    const name = target.name
    setFormData((formData) => {
      const newFormData = JSON.parse(JSON.stringify(formData))
      newFormData[name] = value
      return newFormData
    })
  }

  useEffect(() => {
    if (success) {
      setFormData(defaultFormData)
    }
  }, [success])

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = 'auto'
    }
    return () => {
      document.documentElement.style.overflow = 'auto'
    }
  }, [isOpen])

  return (
    <div className={`FormModal ${isOpen ? 'FormModal--opened' : ''}`}>
      <div
        className="darkness"
        onClick={toggleIsOpen}
        onKeyPress={toggleIsOpen}
        role="button"
        tabIndex={0}
      />
      <form
        name="contact-us"
        id="contact-us-form"
        className="FormModal__content"
        data-netlify="true"
        data-netlify-recaptcha="true"
        onSubmit={(e) =>
          onFormSubmitValidateRecaptcha(
            e,
            setSubmitting,
            setSubmitResult,
            setNewsletterSubmitResult,
            setSuccess,
          )
        }
        onFocus={loadRecaptcha}
      >
        <input type="hidden" name="form-name" value="contact-us"></input>
        <FontAwesomeIcon
          className="FormModal__close-button"
          icon={faTimes}
          onClick={toggleIsOpen}
          onKeyPress={toggleIsOpen}
          role="button"
          tabIndex={0}
        />
        <div className="FormModal__title">Contact Us</div>
        <div className="FormModal__fieldsContainer">
          <FormRow
            halfSize={true}
            inputType={InputType.text}
            inputName={'first_name'}
            required={true}
            placeholder="Enter first name"
            label="First Name"
            value={first_name}
            onChange={handleChange}
          />
          <FormRow
            halfSize={true}
            inputType={InputType.text}
            inputName={'last_name'}
            required={true}
            placeholder="Enter last name"
            label="Last Name"
            value={last_name}
            onChange={handleChange}
          />
          <FormRow
            halfSize={false}
            inputType={InputType.text}
            inputName={'company_name'}
            required={true}
            placeholder="Enter company name"
            label="Company"
            value={company_name}
            onChange={handleChange}
          />
          <FormRow
            halfSize={false}
            inputType={InputType.email}
            inputName={'email'}
            required={true}
            placeholder="Enter work email"
            label="Email Address"
            value={email}
            onChange={handleChange}
          />
          <FormRow
            halfSize={false}
            label="How can we help you?"
            inputType={InputType.textarea}
            inputName={'message'}
            required={true}
            placeholder="Enter message here"
            rows={5}
            value={message}
            onChange={handleChange}
          />
          <FormRow
            halfSize={false}
            label="Sign up for our newsletter to receive actionable insights to help
            you directly measure and prove ROI from VR/AR experiences."
            inputType={InputType.checkbox}
            inputName={'newsletter_signup'}
            required={false}
            placeholder="Enter message here"
            checked={newsletter_signup}
            onChange={handleChange}
          />
          <FormRow
            halfSize={false}
            label={
              <>
                You consent to Cognitive3D processing your personal data in
                accordance with our{' '}
                <a href="/privacy-policy/" target="_blank" rel="noreferrer">
                  Privacy Policy.
                </a>
              </>
            }
            inputType={InputType.checkbox}
            inputName={'consent'}
            required={true}
            placeholder="Enter message here"
            checked={consent}
            onChange={handleChange}
          />
        </div>
        <input type="hidden" name="form-name" value="contact-us" />
        {isServer || !isOpen || !recaptchaLoaded ? null : (
          <Recaptcha sitekey={process.env.GATSBY_GOOGLE_RECAPTCHA_SITE_KEY} />
        )}
        <button
          type="submit"
          className="btn btn-light-violet FormModal__submit"
        >
          {submitting ? (
            <ThreeDots color="#ffffff" height={18} width={50} />
          ) : (
            `Submit`
          )}
        </button>
        {!!submitResult && (
          <div
            style={{
              textAlign: 'center',
              marginTop: '10px',
              color: success ? 'green' : 'red',
            }}
          >
            {submitResult}
          </div>
        )}
        {!!newsletterSubmitResult && (
          <div
            style={{
              textAlign: 'center',
              marginTop: '10px',
              color: success ? 'green' : 'red',
            }}
            dangerouslySetInnerHTML={{ __html: newsletterSubmitResult }}
          />
        )}
      </form>
    </div>
  )
}

export default ContactUsForm
